import { ErrorResponse } from "@apollo/client/link/error";
import { SerializedError } from "@reduxjs/toolkit";
import { GraphQLFormattedError } from "graphql/error/formatError";
import { castArray, omit } from "lodash";

interface NotFoundError extends GraphQLFormattedError {
  extensions: {
    code: "NOT_FOUND";
  };
}

interface UnauthorizedError extends GraphQLFormattedError {
  extensions: {
    code: "UNAUTHORIZED";
    details: Record<string, string[]>;
    fullMessages: string[];
  };
}

interface InvalidError extends GraphQLFormattedError {
  extensions: {
    code: "INVALID";
    errors: ValidationError[];
  };
}

interface ValidationError {
  attribute: string;
  type: string;
  message: string;
  fullMessage: string;
}

function isApolloErrorResponse(error: unknown): error is ErrorResponse {
  return (
    "networkError" in (error as ErrorResponse) ||
    "graphQLErrors" in (error as ErrorResponse)
  );
}

function extractNetworkError(
  error: Exclude<ErrorResponse["networkError"], null | undefined>
) {
  if ("result" in error) {
    if (typeof error.result === "string") {
      return { message: error.result };
    } else {
      return error.result.errors as GraphQLFormattedError[];
    }
  }
  return error;
}

export function isGraphqlError(error: unknown): error is GraphQLFormattedError {
  const err = error as GraphQLFormattedError;
  return "locations" in err || "path" in err;
}

const fetchFailedErrors = new Set([
  "network error", // Chrome
  "Failed to fetch", // Chrome
  "NetworkError when attempting to fetch resource.", // Firefox
  "The Internet connection appears to be offline.", // Safari 16
  "Load failed", // Safari 17+
  "Network request failed", // `cross-fetch`
  "fetch failed", // Undici (Node.js)
]);

// Code taken from https://github.com/sindresorhus/is-network-error/blob/main/index.js
export function isOfflineError(error: any): boolean {
  const valid = error.name === "TypeError" && typeof error.message === "string";
  if (!valid) return false;
  return fetchFailedErrors.has(error.message);
}

export function getBaseErrorMessages(errors: ValidationError[]): string {
  const baseErrors = errors.filter((e) => e.attribute === "base");
  return baseErrors.map((e) => `${e.message}. `).join("");
}

/**
 * Checks if error is an internal error, whether frontend error like syntax errors and such or
 * missing null checks or internal server error.
 */
export function isInternalError(error: any): boolean {
  if (error.networkError) {
    const networkError = extractNetworkError(error.networkError);
    return castArray(networkError).some(
      (err: any) => err.extensions.code === "INTERNAL_SERVER_ERROR"
    );
  }

  if (error.graphQLErrors) {
    return error.graphQLErrors.some(
      (err: any) => err.extensions.code === "INTERNAL_SERVER_ERROR"
    );
  }

  // Treat all non-network related errors as internal error, as those should be caught by devs
  // while developing.
  return true;
}

const commonProperties: Array<keyof SerializedError> = [
  "name",
  "message",
  "stack",
  "code",
];
/**
 * Custom `serializeError` for graphql errors to be used in `createAsyncThunk`. This function is
 * generic enough to be used outside `createAsyncThunk`, just plop an error in it even if not coming
 * from graphql and it will have a SerializedError object.
 */
export function serializeError(error: unknown): SerializedError {
  if (isOfflineError(error)) {
    return {
      code: "INTERNET_DISCONNECTED_ERROR",
      message: "The Internet connection appears to be offline.",
      name: "Error",
    };
  }

  if (isApolloErrorResponse(error)) {
    if (error.networkError) {
      const err = extractNetworkError(error.networkError);
      if (err == null) {
        // There is no actual network error here (maybe). Let's continue to other paths by removing
        // networkError property from error object.
        return serializeError(omit(error, "networkError"));
      }
      return serializeError(err);
    }
    if (error.graphQLErrors) {
      return serializeError(error.graphQLErrors);
    }
  }

  if (Array.isArray(error)) {
    if (error.length === 1) {
      return serializeError(error[0]);
    } else {
      const errors = error.map((err) => serializeError(err));

      // a custom error code signifying multiple errors happened.
      return {
        code: "MULTIPLE_ERRORS",
        message: errors.map((err) => err.message).join(". "),
        name: "Error",
      };
    }
  }

  if (isGraphqlError(error)) {
    return {
      code: error.extensions?.code,
      message: error.message,
      name: "GraphQLError",
      stack: error.extensions?.exception?.stacktrace?.join("\n"),
    };
  }

  if ("error" in (error as any)) {
    // Extract inner error and treat this as an API response error.
    const err = (error as any).error;
    err.code = "API_ERROR";

    return serializeError(err);
  }

  if ("errors" in (error as any)) {
    // Extract inner error and treat this as an API response error.
    const errors = (error as any).errors.map((e: any) => ({
      code: "API_ERROR",
      ...e,
    }));

    return serializeError(errors);
  }

  if (typeof error === "object" && error !== null) {
    const simpleError: SerializedError = {};
    for (const property of commonProperties) {
      if (typeof (error as any)[property] === "string") {
        simpleError[property] = (error as any)[property];
      }
    }

    return simpleError;
  }

  return { message: String(error) };
}

/**
 * Gets the error message from a serialized error, or a generic message if it is
 * an internal error.
 */
function formatSerializedError(
  error: SerializedError,
  genericMessage: string
): string {
  // If error does not have code, we'll treat it as an internal error too.
  if (error.code == null || error.code === "INTERNAL_SERVER_ERROR") {
    console.error(Object.assign(new Error(), error));
    return genericMessage;
  } else {
    return error.message || genericMessage;
  }
}

export function showInvalidError(error: any, invalidMessage: string): string {
  if (isInvalidError(serializeError(error))) {
    return invalidMessage;
  } else {
    return displayError(error);
  }
}

export function displayError(
  error: unknown,
  genericMessage = "Something went wrong. Please try again later."
): string {
  return formatSerializedError(serializeError(error), genericMessage);
}

export function getValidationErrors(
  error: GraphQLFormattedError | InvalidError
): ValidationError[] {
  if (isInvalidError(error)) {
    return error.extensions.errors ?? [];
  } else {
    return [];
  }
}

export function isInvalidError(error: SerializedError): error is InvalidError {
  return error.code === "INVALID";
}

export function parseInvalidError(error: unknown): InvalidError | undefined {
  if (!isApolloErrorResponse(error)) return;

  const graphqlError = error.graphQLErrors?.[0];
  if (!isGraphqlError(graphqlError) || !isInvalidError(graphqlError)) return;

  return graphqlError;
}

export function isNotFoundError(
  error: GraphQLFormattedError
): error is NotFoundError {
  return error.extensions?.code === "NOT_FOUND";
}

export function isUnauthorizedError(
  error: GraphQLFormattedError
): error is UnauthorizedError {
  return error.extensions?.code === "UNAUTHORIZED";
}
